import { ref } from 'vue'
import { useAlertMessage } from '@/composables/useConfirmMessage'

/**
 * window popup helper
 * - 팝업 차단 감지 시 기본으로 showError('팝업 차단을 해제해주세요')
 * - 같은 name(key)로 중복 오픈 방지(기존 팝업 재사용 + focus)
 */
export function useWindowPopup() {
    // setup() 안에서만 호출되는 걸 전제로(일반적인 composable 패턴)
    const { showError } = useAlertMessage()

    // name(key)별로 열린 팝업을 관리
    const popupMap = ref(new Map()) // Map<string, Window>

    const toQueryString = (query = {}) => {
        const params = new URLSearchParams()
        Object.entries(query).forEach(([k, v]) => {
            if (v === undefined || v === null || v === '') return
            params.append(k, String(v))
        })
        const qs = params.toString()
        return qs ? `?${qs}` : ''
    }

    const buildFeatures = (opts = {}) => {
        const {
            width = 1000,
            height = 800,
            left,
            top,
            resizable = true,
            scrollbars = true,
            fullscreen = false
        } = opts

        if (fullscreen) {
            const w = window.screen.availWidth
            const h = window.screen.availHeight
            return [
                `width=${w}`,
                `height=${h}`,
                `left=0`,
                `top=0`,
                `resizable=yes`,
                `scrollbars=${scrollbars ? 'yes' : 'no'}`
            ].join(',')
        }

        const _left =
            left ??
            (window.screenX || window.screen.left || 0) +
            ((window.outerWidth || 1200) - width) / 2
        const _top =
            top ??
            (window.screenY || window.screen.top || 0) +
            ((window.outerHeight || 800) - height) / 2

        return [
            `width=${width}`,
            `height=${height}`,
            `left=${Math.round(_left)}`,
            `top=${Math.round(_top)}`,
            `resizable=${resizable ? 'yes' : 'no'}`,
            `scrollbars=${scrollbars ? 'yes' : 'no'}`
        ].join(',')
    }

    /**
     * @param {string} url - base url (e.g. /rsvt-info-popup)
     * @param {object} options
     *  - name: 팝업 window name (중복 방지 key로 사용) (default: 'Popup')
     *  - query: {k:v}
     *  - focus: true/false
     *  - reuse: true면 이미 열려있으면 새로 열지 않고 focus + (옵션) url 갱신
     *  - replaceUrlOnReuse: reuse 시 url도 갱신할지 여부
     */
    const openPopup = (url, options = {}) => {
        const {
            name = 'Popup',
            query,
            focus = true,
            reuse = true,
            replaceUrlOnReuse = true,
            customHeader = false
        } = options

        const mergedQuery = { ...query, ...(customHeader ? { customHeader: 'true' } : {}) }
        const fullUrl = `${url}${toQueryString(mergedQuery)}`
        const features = buildFeatures(options)

        // 중복 오픈 방지: 같은 name 팝업이 살아있으면 재사용
        const existing = popupMap.value.get(name)
        if (reuse && existing && !existing.closed) {
            if (replaceUrlOnReuse) {
                // cross-origin이 아니면 location 변경 가능
                try {
                    existing.location.href = fullUrl
                } catch (e) {
                    alert('팝업의 URL을 변경할 수 없습니다. (다른 도메인 열림)')
                }
            }
            if (focus) existing.focus()
            return existing
        }

        const popup = window.open(fullUrl, name, features)

        // 팝업 차단 감지
        if (!popup) {
            // 기본 동작: showError
            try {
                showError('팝업 차단을 해제해주세요')
            } catch (e) {
                // 혹시 provider/inject 문제 있으면 브라우저 alert로 최소 보장
                window.alert('팝업 차단을 해제해주세요')
            }
            return null
        }

        popupMap.value.set(name, popup)
        if (focus) popup.focus()
        return popup
    }

    const closePopup = (name = 'Popup') => {
        const w = popupMap.value.get(name)
        if (w && !w.closed) w.close()
        popupMap.value.delete(name)
    }

    const closeAllPopups = () => {
        popupMap.value.forEach((w, key) => {
            if (w && !w.closed) w.close()
        })
        popupMap.value.clear()
    }

    return { openPopup, closePopup, closeAllPopups }
}
